import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Users,
  Trophy,
  User,
  LogOut,
  UserCheck,
  ShieldCheck,
  Repeat,
  FileSpreadsheet,
  Calendar,
  Flag,
  Layers,
  Shield,
  ChevronDown,
  ChevronsUpDown,
  ClipboardList,
  BookOpen,
  Tag,
  Power,
  Building2,
  Target,
  LayoutGrid,
  Palette,
  Scale,
  FileSignature,
  Archive,
  UserCircle,
  Settings,
  Wrench,
  DollarSign,
  RefreshCcw,
  Shirt,
  Package,
  Receipt,
  FileText,
  Gift,
  LayoutDashboard,
  CreditCard,
  Wallet,
  Inbox,
  Fingerprint,
  Lock,
  Boxes,
  ArrowLeftRight,
  ClipboardCheck,
  AlertTriangle,
  FileBarChart,
  CalendarCheck,
  CalendarRange,
  HelpCircle,
  Truck,
  Mail,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { UpsellModal } from '../billing/Upsell';
import { useModal } from '../../hooks/useModal';
import { usePlatformLegal } from '../../hooks/usePlatformLegal';
import LegalDocLink from '../legal/LegalDocLink';
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarGroup,
  useSidebar,
  Badge
} from '../ui';
import Modal from '../ui/Modal';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { getMisInvitacionesTorneo } from '../../lib/services/torneoInvitaciones';
import { signOut, getStaffGroups, getAllGroups, closeSessionsNow, getClosableSessionsSummary, recalculatePlayerMetadata, recalcularDescuentosDeLaOrg, getSolicitudesPendientesCount, getRetencionResumen, aplicarRetencion, RETENCION_MESES_DEFAULT } from '../../lib/supabase';
import { cn } from '../../lib/utils';

const MAX_GROUP_SHORTCUTS = 8;

const AppSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, staff, isPlatformAdmin, loading: authLoading } = useAuth();
  const { role, isAdmin, isHeadCoach, isPlatformOnlyView, canManagePrograms, canManageTorneos } = usePermissions();
  const { org, orgName, orgShortName, orgLogo, orgIconBackground, isOrgOverridden, myOrgs, switchOrg } = useOrganization();
  const { hasModule } = useEntitlements();
  const [upsellFeature, setUpsellFeature] = React.useState(null);
  const { modal, showConfirm, showSuccess, showError, closeModal } = useModal();
  const { avisoMyklubb } = usePlatformLegal();
  const { open, setOpen, isMobile } = useSidebar();
  const [collapsedSections, setCollapsedSections] = React.useState({
    principal: false,
    agendas: true,
    entrenamientos: false,
    torneos: true,
    pagos: true,
    uniformes: true,
    inventario: true,
    administracion: true,
    herramientas: true,
    platform: true,
  });
  const [myGroups, setMyGroups] = React.useState([]);
  const [loadingSidebarGroups, setLoadingSidebarGroups] = React.useState(false);
  const [showMyGroups, setShowMyGroups] = React.useState(false);
  const [closeSessionsModal, setCloseSessionsModal] = React.useState({
    open: false,
    title: 'Terminar sesiones',
    message: '',
    type: 'info'
  });
  const [closingSessions, setClosingSessions] = React.useState(false);
  const [pendingCount, setPendingCount] = React.useState(0);
  const [invitacionesPendientes, setInvitacionesPendientes] = React.useState(0);

  // "Nuevo" en MYKLUBB Torneos: se apaga solo la primera vez que el usuario entra.
  // Un distintivo permanente deja de leerse a la semana; este cumple su trabajo y se
  // quita. Por usuario, no por navegador: en un club se comparte la computadora.
  const claveVisto = user?.id ? `mk_torneos_visto:${user.id}` : null;
  const [mkTorneosNuevo, setMkTorneosNuevo] = React.useState(false);

  React.useEffect(() => {
    if (!claveVisto) { setMkTorneosNuevo(false); return; }
    try {
      setMkTorneosNuevo(window.localStorage.getItem(claveVisto) !== '1');
    } catch {
      setMkTorneosNuevo(false); // sin localStorage no insistimos
    }
  }, [claveVisto]);

  const marcarMkTorneosVisto = React.useCallback(() => {
    if (!claveVisto) return;
    setMkTorneosNuevo(false);
    try { window.localStorage.setItem(claveVisto, '1'); } catch { /* da igual */ }
  }, [claveVisto]);
  const sectionRefs = React.useRef({});

  React.useEffect(() => {
    if (!org?.id) {
      setPendingCount(0);
      return;
    }
    let cancelled = false;
    const fetchCount = async () => {
      const { count } = await getSolicitudesPendientesCount(org.id);
      if (!cancelled) setPendingCount(count || 0);
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [org?.id]);

  // Invitaciones sin responder. Una invitacion que caduca sin que nadie la viera es
  // un torneo que el club se perdio, asi que el numero vive en el menu.
  React.useEffect(() => {
    if (!org?.id) { setInvitacionesPendientes(0); return; }
    let cancelled = false;
    const fetchInvitaciones = async () => {
      const { data } = await getMisInvitacionesTorneo();
      if (!cancelled) setInvitacionesPendientes((data || []).length);
    };
    fetchInvitaciones();
    const interval = setInterval(fetchInvitaciones, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [org?.id]);

  const handleCloseSessions = async () => {
    const { data: summary, error: summaryError } = await getClosableSessionsSummary();
    if (summaryError) {
      showError('Error', 'No pudimos calcular las sesiones por cerrar.');
      return;
    }
    const confirmed = await showConfirm(
      'Terminar sesiones',
      `Se cerrarán las sesiones anteriores a hoy.\nEntrenamientos: ${summary?.entrenamientos ?? 0}\nJuegos: ${summary?.juegos ?? 0}\nTotal: ${summary?.total ?? 0}\n¿Deseas continuar?`,
      'Terminar',
      'Cancelar'
    );
    if (!confirmed) return;
    setCloseSessionsModal({
      open: true,
      title: 'Terminando sesiones',
      message: 'Estamos cerrando las sesiones anteriores a hoy...',
      type: 'info'
    });
    setClosingSessions(true);
    const { data, error } = await closeSessionsNow();
    setClosingSessions(false);
    if (error) {
      setCloseSessionsModal({
        open: true,
        title: 'Error al terminar sesiones',
        message: error.message || 'No pudimos cerrar las sesiones.',
        type: 'error'
      });
      return;
    }
    setCloseSessionsModal({
      open: true,
      title: 'Sesiones terminadas',
      message: `Entrenamientos: ${data?.cerradas_entrenamientos ?? 0}\nJuegos: ${data?.cerradas_juegos ?? 0}\nTotal: ${data?.cerradas ?? 0}`,
      type: 'success'
    });
  };

  const handleAplicarRetencion = async () => {
    const meses = RETENCION_MESES_DEFAULT;
    const { data: resumen, error: resErr } = await getRetencionResumen(org?.id, meses);
    if (resErr) {
      showError('Error', 'No pudimos calcular la retención.');
      return;
    }
    if (!resumen || resumen.documentos === 0) {
      showSuccess('Nada por purgar', `No hay documentos de jugadores dados de baja o inactivos hace más de ${meses} meses.`);
      return;
    }
    const confirmed = await showConfirm(
      'Aplicar retención',
      `Se purgarán ${resumen.documentos} documento(s) de ${resumen.jugadores} jugador(es) dados de baja o inactivos hace más de ${meses} meses.\n\nEsta acción es IRREVERSIBLE. ¿Continuar?`,
      'Purgar',
      'Cancelar'
    );
    if (!confirmed) return;
    const { data, error } = await aplicarRetencion(org?.id, meses);
    if (error) {
      showError('Error', error.message || 'No pudimos aplicar la retención.');
      return;
    }
    showSuccess('Retención aplicada', `Documentos purgados: ${data?.documentos ?? 0}\nJugadores: ${data?.jugadores ?? 0}`);
  };

  // Ojo: ya NO recalcula deportes (ese cache se retiro y hoy la inscripcion manda).
  // Lo unico que escribe es jugadores_meta.categorias_por_grupo, que consumen el
  // detalle de grupo y el tarjeton.
  const handleRecalcMetadata = async () => {
    const confirmed = await showConfirm(
      'Recalcular categorías',
      'Se recalculará la categoría de cada jugador en cada uno de sus grupos, según sus grupos actuales.\n\nÚsalo si el detalle de un grupo o un tarjetón muestran una categoría que ya no corresponde.\n\nPuede tardar unos segundos.',
      'Recalcular',
      'Cancelar'
    );
    if (!confirmed) return;
    try {
      const { data, error } = await recalculatePlayerMetadata({});
      if (error) throw error;
      showSuccess('Listo', `Categorías recalculadas.\nCreadas: ${data?.created ?? 0}\nActualizadas: ${data?.updated ?? 0}`);
    } catch (error) {
      showError('Error', 'No se pudo recalcular: ' + (error?.message || 'Error desconocido'));
    }
  };

  // El motor es idempotente, asi que esto se puede correr cuantas veces haga
  // falta: si todo esta al dia no cambia nada. Sirve cuando algo quedo desfasado
  // -- por ejemplo si cambio la posicion de un hijo en su familia.
  const handleRecalcDescuentos = async () => {
    const { data: previo } = await recalcularDescuentosDeLaOrg({ dryRun: true });
    const cambios = previo?.cambios?.length || 0;
    const confirmed = await showConfirm(
      'Recalcular descuentos',
      cambios
        ? `Se ajustarán ${cambios} cargo(s) de quienes tienen una promoción asignada.\n\nLos cargos que ya recibieron pagos no se modifican: el descuento les llega como saldo a favor.`
        : 'Todo está al día: no hay ningún cargo por ajustar.\n\n¿Correrlo de todos modos?',
      'Recalcular',
      'Cancelar'
    );
    if (!confirmed) return;
    try {
      const { data, error } = await recalcularDescuentosDeLaOrg({});
      if (error) throw error;
      const partes = [`Cargos ajustados: ${data?.cambios?.length ?? 0}`];
      if (data?.historia?.length) partes.push(`Con pagos, sin tocar: ${data.historia.length}`);
      if (data?.saldoGenerado > 0) partes.push(`Saldo a favor generado: $${Number(data.saldoGenerado).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`);
      showSuccess('Listo', partes.join('\n'));
    } catch (error) {
      showError('Error', 'No se pudo recalcular: ' + (error?.message || 'Error desconocido'));
    }
  };

  const getRoleDisplayName = (rol) => {
    switch (rol) {
      case 'platform_admin':
        return 'Administrador de Plataforma';
      case 'admin':
        return 'Administrador';
      case 'head_coach':
        return 'Head Coach';
      case 'manager':
        return 'Manager';
      case 'coach':
        return 'Coach';
      case 'assistant':
        return 'Asistente';
      default:
        return rol ? `Staff (${rol})` : 'Staff';
    }
  };

  React.useEffect(() => {
    const loadSidebarGroups = async () => {
      if (!user) return;
      if (!isAdmin && !staff?.id) return;
      setLoadingSidebarGroups(true);
      try {
        if (isAdmin) {
          const { data, error } = await getAllGroups();
          if (error) throw error;
          const sorted = (data || [])
            .filter((group) => (group.estatus || '').toLowerCase() === 'activo')
            .sort((a, b) =>
              (a.nombre_del_grupo || '').localeCompare(b.nombre_del_grupo || '')
            );
          setMyGroups(sorted);
        } else {
          const { data, error } = await getStaffGroups(staff.id);
          if (error) throw error;
          const formatted = (data || [])
            .map((item) => item.grupos)
            .filter(Boolean)
            .filter((group) => (group.estatus || '').toLowerCase() === 'activo')
            .sort((a, b) => (a.nombre_del_grupo || '').localeCompare(b.nombre_del_grupo || ''));
          setMyGroups(formatted);
        }
      } catch (error) {
        console.error('Error loading sidebar groups:', error);
      } finally {
        setLoadingSidebarGroups(false);
      }
    };

    loadSidebarGroups();
  }, [isAdmin, staff?.id, user?.id]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesion:', error);
    }
  };

  const getCurrentActiveItem = () => {
    const path = location.pathname;
    if (path === '/agenda') return 'agenda-entrenamientos';
    if (path === '/programas/hoy') return 'programa-hoy';
    if (path === '/torneos/agenda') return 'agenda-torneos';
    if (path.startsWith('/programas/biblioteca')) return 'programas-biblioteca';
    if (path.startsWith('/programas/habilidades')) return 'programas-habilidades';
    if (path.startsWith('/programas/plantillas-secciones')) return 'programas-plantillas';
    if (path.startsWith('/programas')) return 'programas-agenda';
    if (path === '/hoy' || path.startsWith('/attendance')) return 'hoy';
    if (path === '/groups' || path === '/') return 'groups';
    if (path === '/admin/jugadores') return 'jugadores';
    if (path === '/admin/jugadores/incompletas') return 'fichas-incompletas';
    if (path === '/admin/consentimientos') return 'consentimientos';
    if (path === '/admin/pagos') return 'pagos-dashboard';
    if (path === '/admin/pagos/registro') return 'pagos-registro';
    if (path === '/admin/pagos/estado-cuenta') return 'pagos-estado-cuenta';
    if (path === '/admin/pagos/planes') return 'pagos-planes';
    if (path === '/admin/pagos/conceptos') return 'pagos-conceptos';
    if (path === '/admin/pagos/asignaciones') return 'pagos-asignaciones';
    if (path === '/admin/pagos/promociones') return 'pagos-promociones';
    if (path === '/admin/pagos/solicitudes') return 'pagos-solicitudes';
    if (path === '/admin/pagos/familias') return 'familias';
    if (path === '/admin/uniformes') return 'uniformes-prendas';
    if (path === '/admin/uniformes/tallas') return 'uniformes-tallas';
    if (path === '/admin/uniformes/reparto') return 'uniformes-reparto';
    if (path === '/admin/uniformes/extras') return 'uniformes-extras';
    if (path === '/admin/uniformes/temporada') return 'uniformes-temporada';
    if (path === '/admin/uniformes/pedidos') return 'uniformes-pedidos';
    if (path === '/admin/inventario/jugadores') return 'inventario-jugadores';
    if (path === '/admin/inventario/prestamos') return 'inventario-prestamos';
    if (path.startsWith('/admin/inventario/conteos')) return 'inventario-conteos';
    if (path === '/admin/inventario/reporte') return 'inventario-reporte';
    if (path === '/admin/inventario') return 'inventario-articulos';
    if (path.startsWith('/admin/torneos')) return 'torneos-mis';
    if (path === '/agenda/juegos') return 'agenda-juegos';
    if (path.startsWith('/torneos/juegos')) return 'agenda-torneos';
    if (path === '/torneos/agenda') return 'agenda-torneos';
    // Antes del catch-all de /torneos: si no, /torneos/ligas marcaria "Torneos".
    if (path === '/torneos/ligas' || path === '/catalogos/ligas') return 'catalogos-ligas';
    if (path === '/torneos/equipos' || path === '/catalogos/equipos') return 'catalogos-equipos';
    if (path === '/torneos-myklubb/juegos') return 'mkt-juegos';
    if (path === '/torneos-myklubb/ligas') return 'mkt-ligas';
    if (path === '/torneos-myklubb/invitaciones' || path === '/torneos/invitaciones') return 'mkt-invitaciones';
    if (path.startsWith('/torneos-myklubb') || path.startsWith('/torneos/externos')) return 'mkt-torneos';
    if (path.startsWith('/torneos')) return 'torneos-mis';
    if (path === '/entrenamientos/asistencia-mensual') return 'reporte-mensual';
    if (path === '/admin/staff') return 'staff';
    if (path === '/admin/groups') return 'group-management';
    if (path === '/admin/entrenamientos-regulares') return 'series-entrenamiento';
    if (path === '/admin/temporadas') return 'temporadas';
    if (path === '/admin/inscripciones') return 'inscripciones';
    if (path === '/admin/categorias') return 'categorias';
    if (path === '/admin/organizacion') return 'organizacion-settings';
    if (path === '/platform') return 'platform-dashboard';
    if (path === '/platform/cross-org') return 'platform-identidades';
    if (path === '/platform/organizations') return 'platform-orgs';
    if (path === '/platform/branding') return 'platform-branding';
    if (path === '/platform/legal') return 'platform-legal';
    return '';
  };

  const activeItem = getCurrentActiveItem();


  // Al expandir una sección (o "Mis grupos"), hacer scroll para que ella y todos
  // sus hijos queden visibles dentro del área scrolleable del menú.
  React.useEffect(() => {
    let targetKey = null;
    if (showMyGroups) targetKey = 'myGroups';
    else targetKey = Object.keys(collapsedSections).find((k) => !collapsedSections[k]);
    const el = targetKey && sectionRefs.current[targetKey];
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }
  }, [collapsedSections, showMyGroups]);

  // La marca de MYKLUBB para la seccion que ES de MYKLUBB. El icono "-light" es el de
  // relleno oscuro: va sobre fondo claro, que es el del sidebar.
  const MyklubbMark = () => (
    <img src="/assets/myklubb-icon-light.svg" alt="MYKLUBB" className="h-2.5 w-auto shrink-0" />
  );

  const sectionIcons = {
    agendas: Calendar,
    entrenamientos: ClipboardList,
    torneos: Trophy,
    mkTorneos: MyklubbMark,
    pagos: DollarSign,
    uniformes: Shirt,
    inventario: Boxes,
    administracion: Shield,
    herramientas: Wrench,
  };

  const mainMenuItems = [
    { id: 'hoy', label: 'Hoy', icon: CalendarCheck, path: '/hoy' },
  ];

  // Consulta, no captura: el mes completo con los juegos de los DOS mundos, cada uno
  // con su etiqueta. Como el calendario del telefono: ahi cabe todo porque nadie
  // captura nada. Juegos (crear, editar, marcador) se fue a Torneos internos.
  const agendasMenuItems = [
    { id: 'agenda-entrenamientos', label: 'Entrenamientos', icon: Calendar, path: '/agenda' },
    { id: 'agenda-juegos', label: 'Calendario de juegos', icon: CalendarRange, path: '/agenda/juegos' },
  ];

  const trainingMenuItems = React.useMemo(() => {
    const base = [
      { id: 'programa-hoy', label: 'Programa de hoy', icon: BookOpen, path: '/programas/hoy' },
      { id: 'programas-agenda', label: 'Programas', icon: ClipboardList, path: '/programas/agenda', programsManageOnly: true },
      { id: 'reporte-mensual', label: 'Asistencia mensual', icon: FileSpreadsheet, path: '/entrenamientos/asistencia-mensual' }
    ];
    if (canManagePrograms) {
      // Gestion de programas: visible para quien puede gestionarlos (admin y
      // head_coach). Las rutas ya usan canManagePrograms, el menu debe coincidir.
      base.push({
        id: 'programas-biblioteca',
        label: 'Biblioteca',
        icon: BookOpen,
        path: '/programas/biblioteca'
      });
      base.push({
        id: 'programas-habilidades',
        label: 'Habilidades',
        icon: Target,
        path: '/programas/habilidades'
      });
      base.push({
        id: 'programas-plantillas',
        label: 'Plantillas de seccion',
        icon: LayoutGrid,
        path: '/programas/plantillas-secciones'
      });
      base.push({
        id: 'series-entrenamiento',
        label: 'Horarios',
        icon: Repeat,
        path: '/admin/entrenamientos-regulares'
      });
    }
    if (!hasModule('programs')) {
      // 'reporte-mensual' (asistencia) es core, no se bloquea por el modulo de programas
      return base.map((item) => (item.id === 'reporte-mensual' ? item : { ...item, locked: true }));
    }
    return base;
  }, [canManagePrograms, hasModule]);

  // Dos mundos, dos bloques al nivel del menu: el usuario ubica que hay dos tipos de
  // torneo sin que nadie se lo explique. Equipos rivales y Ligas viven aqui porque son
  // los catalogos que el club CREA para sus torneos propios: en uno de la red los
  // equipos los pone la organizadora y la liga ya existe.
  const torneosMenuItems = [
    { id: 'torneos-mis', label: 'Torneos', icon: Trophy, path: '/torneos' },
    { id: 'agenda-torneos', label: 'Juegos', icon: CalendarRange, path: '/torneos/juegos' },
    { id: 'catalogos-ligas', label: 'Ligas', icon: Layers, path: '/torneos/ligas', torneosAdminOnly: true },
    { id: 'catalogos-equipos', label: 'Equipos rivales', icon: Shield, path: '/torneos/equipos', torneosAdminOnly: true }
  ];

  // La red: todo lo que llega de la app de Torneos. Aqui el club no crea nada -- recibe
  // invitaciones, se inscribe, y VE las ligas de las organizadoras donde participa.
  const mkTorneosMenuItems = [
    { id: 'mkt-torneos', label: 'Mis torneos', icon: Trophy, path: '/torneos-myklubb' },
    { id: 'mkt-juegos', label: 'Juegos', icon: CalendarRange, path: '/torneos-myklubb/juegos' },
    { id: 'mkt-invitaciones', label: 'Invitaciones', icon: Mail, path: '/torneos-myklubb/invitaciones', badgeCount: invitacionesPendientes, torneosAdminOnly: true },
    { id: 'mkt-ligas', label: 'Ligas donde participo', icon: Layers, path: '/torneos-myklubb/ligas', torneosAdminOnly: true }
  ];

  const platformMenuItems = [
    { id: 'platform-dashboard', label: 'Dashboard', icon: BarChart3, path: '/platform' },
    { id: 'platform-orgs', label: 'Organizaciones', icon: Building2, path: '/platform/organizations' },
    { id: 'platform-plan-requests', label: 'Solicitudes de plan', icon: CreditCard, path: '/platform/plan-requests' },
    { id: 'platform-identidades', label: 'Identidades', icon: Fingerprint, path: '/platform/cross-org' },
    { id: 'platform-branding', label: 'Marca MYKLUBB', icon: Palette, path: '/platform/branding' },
    { id: 'platform-legal', label: 'Legal', icon: Scale, path: '/platform/legal' },
  ];

  const pagosMenuItems = [
    { id: 'pagos-dashboard', label: 'Resumen', icon: LayoutDashboard, path: '/admin/pagos' },
    { id: 'pagos-registro', label: 'Pagos', icon: CreditCard, path: '/admin/pagos/registro' },
    { id: 'pagos-estado-cuenta', label: 'Estado de cuenta', icon: Wallet, path: '/admin/pagos/estado-cuenta' },
    { id: 'pagos-solicitudes', label: 'Solicitudes', icon: Inbox, path: '/admin/pagos/solicitudes', badgeCount: pendingCount },
    { id: 'pagos-asignaciones', label: 'Asignaciones', icon: ClipboardList, path: '/admin/pagos/asignaciones' },
    { id: 'pagos-planes', label: 'Planes', icon: FileText, path: '/admin/pagos/planes' },
    { id: 'pagos-conceptos', label: 'Conceptos', icon: Receipt, path: '/admin/pagos/conceptos' },
    { id: 'pagos-promociones', label: 'Promociones', icon: Gift, path: '/admin/pagos/promociones' },
  ];

  // Ordenado por FRECUENCIA, no por cronologia: arriba lo que se toca cada semana,
  // al fondo lo que se configura una vez por temporada y no se vuelve a abrir.
  // Antes el orden no significaba nada -- el primer paso del ciclo (el catalogo de
  // prendas) estaba hasta abajo y el ultimo (entregar) hasta arriba.
  const uniformesMenuItems = [
    { id: 'uniformes-tallas', label: 'Tallas', icon: ClipboardCheck, path: '/admin/uniformes/tallas' },
    { id: 'uniformes-pedidos', label: 'Pedidos', icon: Truck, path: '/admin/uniformes/pedidos' },
    { id: 'uniformes-reparto', label: 'Reparto', icon: Package, path: '/admin/uniformes/reparto' },
    { id: 'uniformes-extras', label: 'Extras', icon: Tag, path: '/admin/uniformes/extras' },
    { id: 'uniformes-temporada', label: 'De la temporada', icon: CalendarRange, path: '/admin/uniformes/temporada' },
    { id: 'uniformes-prendas', label: 'Prendas', icon: Shirt, path: '/admin/uniformes' },
  ];

  const inventarioMenuItems = [
    { id: 'inventario-articulos', label: 'Artículos', icon: Boxes, path: '/admin/inventario' },
    { id: 'inventario-prestamos', label: 'Préstamos', icon: ArrowLeftRight, path: '/admin/inventario/prestamos' },
    { id: 'inventario-jugadores', label: 'Por jugador', icon: UserCheck, path: '/admin/inventario/jugadores' },
    { id: 'inventario-conteos', label: 'Conteos', icon: ClipboardCheck, path: '/admin/inventario/conteos' },
    { id: 'inventario-reporte', label: 'Reporte', icon: FileBarChart, path: '/admin/inventario/reporte' },
  ];

  const adminMenuItems = [
    { id: 'jugadores', label: 'Jugadores', icon: User, path: '/admin/jugadores' },
    { id: 'consentimientos', label: 'Consentimientos', icon: FileSignature, path: '/admin/consentimientos' },
    { id: 'familias', label: 'Familias', icon: Users, path: '/admin/pagos/familias' },
    // Temporal: existe para arreglar lo que quedo mal antes de que el responsable
    // fuera obligatorio. Cuando la lista llegue a cero y se mantenga ahi, esta
    // entrada se retira -- no es una pantalla de operacion diaria.
    { id: 'fichas-incompletas', label: 'Fichas por completar', icon: AlertTriangle, path: '/admin/jugadores/incompletas' },
    { id: 'staff', label: 'Staff', icon: UserCheck, path: '/admin/staff' },
    { id: 'group-management', label: 'Grupos', icon: ShieldCheck, path: '/admin/groups' },
    { id: 'categorias', label: 'Categorías', icon: Tag, path: '/admin/categorias' },
    { id: 'temporadas', label: 'Temporadas', icon: Calendar, path: '/admin/temporadas' },
    { id: 'inscripciones', label: 'Inscripciones', icon: ClipboardList, path: '/admin/inscripciones' },
  ];

  const herramientasMenuItems = [
    { id: 'close-sessions', label: 'Terminar sesiones', icon: Power, onClick: handleCloseSessions, adminOnly: true },
    { id: 'recalc-metadata', label: 'Recalcular categorías', icon: RefreshCcw, onClick: handleRecalcMetadata, adminOnly: true },
    { id: 'recalc-descuentos', label: 'Recalcular descuentos', icon: RefreshCcw, onClick: handleRecalcDescuentos, adminOnly: true },
    { id: 'aplicar-retencion', label: 'Aplicar retención', icon: Archive, onClick: handleAplicarRetencion, adminOnly: true },
  ];

  // De que seccion es cada opcion del menu.
  //
  // Sale de los ARREGLOS de arriba, que es donde de verdad vive esa informacion. Antes
  // era una tabla PARALELA de prefijos de id (`itemId.startsWith('torneos')` y demas) con
  // un `return 'principal'` al final para todo lo que no reconociera. Se desincronizaba
  // en silencio cada vez que se agregaba un item cuyo id no seguia la convencion de su
  // seccion, y entonces el efecto de abajo abria "principal" y CERRABA la seccion desde
  // la que se acababa de hacer clic. Paso con "Equipos rivales" (id `catalogos-equipos`,
  // en Torneos internos) y "Fichas por completar" (id `fichas-incompletas`, en
  // Administracion).
  //
  // Derivandolo, un item nuevo queda bien por construccion, se llame como se llame.
  const seccionDeItem = (itemId) => {
    if (!itemId) return 'principal';
    const registro = [
      ['principal', mainMenuItems],
      ['agendas', agendasMenuItems],
      ['entrenamientos', trainingMenuItems],
      ['torneos', torneosMenuItems],
      ['mkTorneos', mkTorneosMenuItems],
      ['pagos', pagosMenuItems],
      ['uniformes', uniformesMenuItems],
      ['inventario', inventarioMenuItems],
      ['administracion', adminMenuItems],
      ['herramientas', herramientasMenuItems],
      ['platform', platformMenuItems],
    ];
    const encontrado = registro.find(([, items]) => (items || []).some((i) => i?.id === itemId));
    // Rutas que no cuelgan de ninguna seccion: /groups (que es el bloque propio "Mis
    // grupos") y /admin/organizacion (que se abre desde el pie del sidebar).
    return encontrado ? encontrado[0] : 'principal';
  };

  // Al cambiar de pantalla, abrir la seccion a la que pertenece y cerrar las demas.
  // Depende SOLO de `activeItem`: si dependiera del registro -- que se rehace en cada
  // render -- pisaria las secciones que el usuario abre a mano.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    const activeSection = seccionDeItem(activeItem);
    setCollapsedSections({
      principal: activeSection !== 'principal',
      agendas: activeSection !== 'agendas',
      entrenamientos: activeSection !== 'entrenamientos',
      torneos: activeSection !== 'torneos',
      mkTorneos: activeSection !== 'mkTorneos',
      pagos: activeSection !== 'pagos',
      uniformes: activeSection !== 'uniformes',
      inventario: activeSection !== 'inventario',
      administracion: activeSection !== 'administracion',
      herramientas: activeSection !== 'herramientas',
      platform: activeSection !== 'platform',
    });
  }, [activeItem]);

  // Acordeón: solo una sección abierta a la vez. Abrir una cierra las demás
  // (incluida "Mis grupos") para que el menú siempre quepa en pantalla.
  const openOnlySection = (sectionId) => {
    setCollapsedSections((prev) =>
      Object.keys(prev).reduce((acc, key) => {
        acc[key] = key !== sectionId; // true = colapsada; solo `sectionId` abierta
        return acc;
      }, {})
    );
    setShowMyGroups(false);
  };

  const collapseAllSections = () => {
    setCollapsedSections((prev) =>
      Object.keys(prev).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {})
    );
  };

  const toggleSection = (sectionId) => {
    if (!open) {
      setOpen(true);
      openOnlySection(sectionId);
      return;
    }
    if (collapsedSections[sectionId]) {
      openOnlySection(sectionId); // estaba cerrada → abrir solo esta
    } else {
      collapseAllSections(); // estaba abierta → cerrarla
    }
  };

  const toggleMyGroups = () => {
    if (!open) {
      setOpen(true);
      setShowMyGroups(true);
      collapseAllSections();
      return;
    }
    setShowMyGroups((prev) => {
      const next = !prev;
      if (next) collapseAllSections(); // abrir "Mis grupos" cierra las secciones
      return next;
    });
  };

  const handleMenuItemClick = (item) => {
    if (item.locked) {
      setUpsellFeature('programs');
      return;
    }
    if (item.disabled) return;
    if (item.onClick) {
      item.onClick();
      return;
    }
    if (item.path) {
      navigate(item.path);
    }
  };

  const filterMenuItems = (items) =>
    items.filter((item) => {
      if (item.adminOnly) return isAdmin;
      if (item.torneosAdminOnly) return canManageTorneos;
      if (item.programsManageOnly) return canManagePrograms;
      return true;
    });

  const renderMenuGroup = (sectionId, title, items, { requireAdmin = false, requirePlatformAdmin = false, moduleFeature = null } = {}) => {
    if (requireAdmin && !isAdmin) return null;
    if (requirePlatformAdmin && !isPlatformAdmin) return null;
    if (moduleFeature && !hasModule(moduleFeature)) {
      const SectionIcon = sectionIcons[sectionId];
      return (
        <SidebarGroup key={sectionId}>
          <button
            type="button"
            onClick={() => setUpsellFeature(moduleFeature)}
            className="flex h-8 w-full items-center justify-between rounded-md px-2 text-xs font-semibold uppercase tracking-wide text-sidebar-foreground/70 hover:text-sidebar-foreground"
            aria-label={title}
            title={!open ? title : undefined}
          >
            {open ? (
              <span>{title}</span>
            ) : (
              <div className="relative flex size-6 items-center justify-center rounded-md bg-muted text-muted-foreground">
                {SectionIcon ? (
                  <SectionIcon className="size-3.5" />
                ) : (
                  <span className="text-[10px] font-bold">{title.slice(0, 1)}</span>
                )}
              </div>
            )}
            <Lock className="size-3.5" />
          </button>
        </SidebarGroup>
      );
    }
    const visibleItems = filterMenuItems(items);
    if (!visibleItems.length) return null;
    const isSectionOpen = !collapsedSections[sectionId];
    const SectionIcon = sectionIcons[sectionId];
    const showPagosBadge = sectionId === 'pagos' && pendingCount > 0;
    // MYKLUBB Torneos se destaca de dos formas: mientras haya invitaciones sin
    // responder manda el numero (es accionable); si no, y el usuario nunca ha entrado,
    // el "Nuevo" que se apaga en cuanto abre la seccion.
    const esMkTorneos = sectionId === 'mkTorneos';
    const showMkCount = esMkTorneos && invitacionesPendientes > 0;
    const showMkNuevo = esMkTorneos && !showMkCount && mkTorneosNuevo;
    const tituloConAviso = showMkCount
      ? `${title} (${invitacionesPendientes} invitaciones sin responder)`
      : (showPagosBadge ? `${title} (${pendingCount} solicitudes pendientes)` : title);

    return (
      <SidebarGroup key={sectionId} ref={(el) => { sectionRefs.current[sectionId] = el; }}>
        <button
          type="button"
          onClick={() => { if (esMkTorneos) marcarMkTorneosVisto(); toggleSection(sectionId); }}
          className="flex h-8 w-full items-center justify-between rounded-md px-2 text-xs font-semibold uppercase tracking-wide text-sidebar-foreground/70 hover:text-sidebar-foreground"
          aria-expanded={isSectionOpen}
          aria-label={title}
        >
          {open ? (
            <span className="flex items-center gap-2">
              {esMkTorneos ? (
                <span className="flex items-center gap-1.5">
                  <MyklubbMark />
                  <span>
                    <span className="font-extrabold tracking-[0.04em]">MYKLUBB</span> Torneos
                  </span>
                </span>
              ) : (
                <span>{title}</span>
              )}
              {showPagosBadge && (
                <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0 hover:bg-amber-500">
                  {pendingCount}
                </Badge>
              )}
              {showMkCount && (
                <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0 hover:bg-amber-500">
                  {invitacionesPendientes}
                </Badge>
              )}
              {showMkNuevo && (
                <Badge className="bg-primary text-primary-foreground text-[9px] px-1.5 py-0 hover:bg-primary">
                  Nuevo
                </Badge>
              )}
            </span>
          ) : (
            <div
              className="relative flex size-6 items-center justify-center rounded-md bg-muted text-muted-foreground"
              title={tituloConAviso}
            >
              {SectionIcon ? (
                <SectionIcon className="size-3.5" />
              ) : (
                <span className="text-[10px] font-bold">{title.slice(0, 1)}</span>
              )}
              {showPagosBadge && (
                <span className="absolute -top-1 -right-1 flex size-2 rounded-full bg-amber-500" />
              )}
              {showMkCount && (
                <span className="absolute -top-1 -right-1 flex size-2 rounded-full bg-amber-500" />
              )}
              {showMkNuevo && (
                <span className="absolute -top-1 -right-1 flex size-2 rounded-full bg-primary" />
              )}
            </div>
          )}
          <ChevronDown
            className={cn(
              'size-4 transition-transform duration-200',
              isSectionOpen ? 'rotate-180' : ''
            )}
          />
        </button>
        <div className={cn(!isSectionOpen && 'hidden')}>
          <SidebarMenuSub>
            {visibleItems.map((item) => (
              <SidebarMenuSubItem key={item.id}>
                <SidebarMenuSubButton
                  Icon={item.icon}
                  isActive={item.path ? activeItem === item.id : false}
                  onClick={() => handleMenuItemClick(item)}
                  disabled={item.disabled}
                  className={item.disabled ? 'opacity-70' : undefined}
                >
                  <span
                    className="flex-1 truncate min-w-0"
                    title={item.disabled ? item.disabledReason : undefined}
                  >
                    {item.label}
                  </span>
                  {item.badgeCount > 0 && (
                    <Badge className="ml-auto bg-amber-500 text-white text-[10px] px-1.5 py-0 hover:bg-amber-500">
                      {item.badgeCount}
                    </Badge>
                  )}
                  {item.locked && (
                    <Lock className="ml-auto size-3.5 text-muted-foreground shrink-0" />
                  )}
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </div>
      </SidebarGroup>
    );
  };

  const renderGroupShortcuts = () => {
    if (loadingSidebarGroups && !myGroups.length) {
      return (
        <div className="px-4 pb-2 text-xs text-muted-foreground">
          Cargando grupos asignados...
        </div>
      );
    }

    // Sin grupos asignados: igual mostrar el acceso a "Mis grupos" como enlace
    // directo (es la página default; el usuario debe poder volver a ella y ver
    // el estado vacío). Antes se ocultaba por completo.
    if (!myGroups.length) {
      return (
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                Icon={Users}
                active={location.pathname === '/groups' || location.pathname === '/'}
                onClick={() => navigate('/groups')}
              >
                Mis grupos
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      );
    }

    const displayGroups = myGroups.slice(0, MAX_GROUP_SHORTCUTS);
    const extraGroups = myGroups.length - displayGroups.length;

    return (
      <SidebarGroup ref={(el) => { sectionRefs.current['myGroups'] = el; }}>
        <button
          type="button"
          onClick={toggleMyGroups}
          className="flex h-8 w-full items-center justify-between rounded-md px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
          aria-expanded={open && showMyGroups}
          aria-label="Mis grupos"
          title={!open ? 'Mis grupos' : undefined}
        >
          {open ? (
            <span className="text-xs font-semibold uppercase tracking-wide">
              Mis grupos
            </span>
          ) : (
            <div className="flex size-6 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <Users className="size-3.5" />
            </div>
          )}
          {open && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground/70">
              {myGroups.length}
              <ChevronDown
                className={cn(
                  'size-3.5 transition-transform duration-200',
                  showMyGroups ? 'rotate-180' : ''
                )}
              />
            </div>
          )}
        </button>
        {open && (
          <div className={cn(!showMyGroups && 'hidden')}>
            <SidebarMenuSub>
              <SidebarMenuSubItem>
                <SidebarMenuSubButton
                  Icon={Users}
                  isActive={location.pathname === '/groups'}
                  onClick={() => navigate('/groups')}
                >
                  Ver todos
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
              {displayGroups.map((group) => {
                const groupPath = `/groups/${group.id}`;
                const isActive = location.pathname === groupPath;
                return (
                  <SidebarMenuSubItem key={group.id}>
                    <SidebarMenuSubButton
                      isActive={isActive}
                      onClick={() => navigate(groupPath)}
                    >
                      {group.nombre_del_grupo || 'Grupo'}
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              })}
            </SidebarMenuSub>
            {extraGroups > 0 && (
              <p className="mt-1 px-5 text-[11px] text-muted-foreground/70">
                +{extraGroups} grupos más.
              </p>
            )}
          </div>
        )}
      </SidebarGroup>
    );
  };

  return (
    <React.Fragment>
      <Sidebar variant="inset" collapsible="icon">
        <SidebarHeader>
          {(() => {
            const headerContent = (
              <div
                className={cn(
                  'flex items-center gap-2 p-2 w-full',
                  !open && 'justify-center'
                )}
              >
                <div
                  className="flex aspect-square size-8 items-center justify-center rounded-lg text-sidebar-primary-foreground"
                  style={{ backgroundColor: orgIconBackground }}
                >
                  <img src={orgLogo} alt={orgName} className="size-4" />
                </div>
                {open && (
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{orgShortName}</span>
                    <span className="truncate text-xs text-muted-foreground">Control</span>
                  </div>
                )}
                {open && myOrgs.length > 1 && (
                  <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
                )}
              </div>
            );
            if (myOrgs.length <= 1) return headerContent;
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full rounded-md text-left transition-colors hover:bg-sidebar-accent">
                    {headerContent}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 rounded-lg" side="bottom" align="start" sideOffset={4}>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">Mis organizaciones</DropdownMenuLabel>
                  {myOrgs.map((o) => (
                    <DropdownMenuItem key={o.org_id} onClick={() => switchOrg(o.org_id)}>
                      <span className="flex-1 truncate">{o.name}</span>
                      {o.org_id === org?.id && <span className="text-xs text-muted-foreground">activa</span>}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })()}
        </SidebarHeader>

        <SidebarContent>
          {authLoading ? null : (
            <>
              {!isPlatformOnlyView && (
                <SidebarGroup>
                  <SidebarMenu>
                    {mainMenuItems.map((item) => (
                      <SidebarMenuItem key={item.id}>
                        <SidebarMenuButton
                          Icon={item.icon}
                          active={activeItem === item.id}
                          onClick={() => handleMenuItemClick(item)}
                        >
                          {item.label}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroup>
              )}
              {!isPlatformOnlyView && renderGroupShortcuts()}
              {!isPlatformOnlyView && renderMenuGroup('agendas', 'Agendas', agendasMenuItems)}
              {!isPlatformOnlyView && renderMenuGroup('entrenamientos', 'Entrenamientos', trainingMenuItems)}
              {!isPlatformOnlyView && renderMenuGroup('torneos', 'Torneos internos', torneosMenuItems)}
              {!isPlatformOnlyView && renderMenuGroup('mkTorneos', 'MYKLUBB Torneos', mkTorneosMenuItems)}
              {!isPlatformOnlyView && renderMenuGroup('pagos', 'Pagos', pagosMenuItems, { requireAdmin: true, moduleFeature: 'payments_module' })}
              {!isPlatformOnlyView && renderMenuGroup('uniformes', 'Uniformes', uniformesMenuItems, { requireAdmin: true, moduleFeature: 'uniforms_module' })}
              {!isPlatformOnlyView && renderMenuGroup('inventario', 'Inventario', inventarioMenuItems, { requireAdmin: true, moduleFeature: 'inventory_module' })}
              {!isPlatformOnlyView && renderMenuGroup('administracion', 'Administración', adminMenuItems, { requireAdmin: true })}
              {!isPlatformOnlyView && renderMenuGroup('herramientas', 'Herramientas', herramientasMenuItems, { requireAdmin: true })}
              {renderMenuGroup('platform', 'Plataforma', platformMenuItems, { requirePlatformAdmin: true })}
            </>
          )}
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md p-2 text-left transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground text-sidebar-foreground data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground',
                      !open && 'justify-center'
                    )}
                  >
                    <Avatar className="size-8 rounded-lg shrink-0">
                      {staff?.foto_url && <AvatarImage src={staff.foto_url} alt={staff?.nombre_completo || 'Usuario'} />}
                      <AvatarFallback className="rounded-lg bg-muted text-muted-foreground">
                        <User className="size-4" />
                      </AvatarFallback>
                    </Avatar>
                    {open && (
                      <>
                        <div className="flex flex-col flex-1 text-left text-sm leading-tight min-w-0">
                          <span className="truncate font-medium text-sidebar-foreground">
                            {staff?.nombre_completo || user?.email || 'Usuario'}
                          </span>
                          <span className="text-muted-foreground truncate text-xs">
                            {/* La etiqueta dice lo mismo que los permisos: la MEMBRESIA en
                                la org activa. Leyendo `staff.rol` decia "Coach" a alguien
                                que manda en el club -- la ficha es un cache de la
                                membresia PRINCIPAL, y miente en cuanto hay dos orgs.
                                `user_metadata` no se usa: lo edita el propio usuario. */}
                            {getRoleDisplayName(isPlatformAdmin && !isOrgOverridden ? 'platform_admin' : role)}
                          </span>
                        </div>
                        <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
                      </>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-56 max-w-[calc(100vw-1rem)] rounded-lg"
                  side={isMobile ? 'top' : 'right'}
                  align={isMobile ? 'start' : 'end'}
                  sideOffset={8}
                  collisionPadding={8}
                >
                  <DropdownMenuLabel className="p-0 font-normal">
                    <div className="flex items-center gap-2 px-2 py-2 text-left text-sm">
                      <Avatar className="size-9 rounded-lg shrink-0">
                        {staff?.foto_url && <AvatarImage src={staff.foto_url} alt={staff?.nombre_completo || 'Usuario'} />}
                        <AvatarFallback className="rounded-lg bg-muted text-muted-foreground">
                          <User className="size-4" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="truncate font-medium">
                          {staff?.nombre_completo || 'Usuario'}
                        </span>
                        <span className="truncate text-xs text-muted-foreground">
                          {staff?.email || user?.email}
                        </span>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem onClick={() => navigate('/perfil')}>
                      <UserCircle />
                      Mi perfil
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => navigate('/admin/organizacion')}>
                        <Settings />
                        Organizacion
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate('/billing/plans')}>
                      <CreditCard />
                      Planes y precios
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/ayuda')}>
                      <HelpCircle />
                      Centro de ayuda
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                    <LogOut />
                    Cerrar sesion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
          <div className={cn(
            'flex items-center gap-1.5 px-2 pb-1',
            !open && 'justify-center'
          )}>
            <img src="/assets/myklubb-icon-light.svg" alt="MYKLUBB" className="size-3 shrink-0 opacity-40" />
            {open && (
              <>
                <span className="text-[10px] font-medium tracking-wider text-muted-foreground opacity-40">MYKLUBB</span>
                {avisoMyklubb && (
                  <LegalDocLink
                    doc={avisoMyklubb}
                    label="Aviso de Privacidad"
                    className="ml-auto text-[10px] text-muted-foreground/60 hover:text-muted-foreground underline"
                  />
                )}
              </>
            )}
          </div>
        </SidebarFooter>
      </Sidebar>

      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        showCancel={modal.showCancel}
      />
      <Modal
        isOpen={closeSessionsModal.open}
        onClose={() => setCloseSessionsModal((prev) => ({ ...prev, open: false }))}
        title={closeSessionsModal.title}
        message={closeSessionsModal.message}
        type={closeSessionsModal.type}
        confirmText={closingSessions ? 'Procesando...' : 'Cerrar'}
        showCancel={false}
        onConfirm={() => setCloseSessionsModal((prev) => ({ ...prev, open: false }))}
      />
      <UpsellModal
        open={!!upsellFeature}
        onOpenChange={(v) => { if (!v) setUpsellFeature(null); }}
        feature={upsellFeature}
      />
    </React.Fragment>
  );
};

export default AppSidebar;
